import { useTranslation } from 'react-i18next';
import { Button, InputPlain } from '@peacecover/component';
import { ArrowLeft, Check } from '@peacecover/icon';
import { useVirtualKeyboardVisible } from '../../chat/hooks/useVirtualKeyboardVisible';
import type { Gender } from '../types';

const GENDER_OPTIONS: { labelKey: string; value: Gender }[] = [
  { labelKey: 'SceneSetting.enterUserInfo.female', value: 'FEMALE' },
  { labelKey: 'SceneSetting.enterUserInfo.male', value: 'MALE' },
  { labelKey: 'SceneSetting.enterUserInfo.nonBinary', value: null }
];

interface ThirdPersonaInputStepProps {
  thirdPersonaName: string;
  thirdPersonaGender: Gender | undefined;
  onThirdPersonaNameChange: (value: string) => void;
  onThirdPersonaGenderChange: (value: Gender) => void;
  onBack: () => void;
  onNext: () => void;
  /** 제출 중 로딩 상태 (버튼 비활성화) */
  isLoading?: boolean;
  isUpdate?: boolean;
}

const ThirdPersonaInputStep = ({
  thirdPersonaName,
  thirdPersonaGender,
  onThirdPersonaNameChange,
  onThirdPersonaGenderChange,
  onBack,
  onNext,
  isLoading = false,
  isUpdate = false
}: ThirdPersonaInputStepProps) => {
  const { t } = useTranslation();
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  return (
    <div className="bg-background-dark min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] overflow-x-hidden relative">
      <header className="px-[16px] py-[15px] z-[1] relative">
        <button type="button" onClick={onBack}>
          <ArrowLeft size={24} className="text-pure-white" />
        </button>
      </header>
      <div className="px-[24px] mt-[10px]">
        <h1 className="text-[26px] font-semibold leading-[31px] text-main-dark-1 whitespace-pre-wrap">
          {isUpdate
            ? t('SceneSetting.changeThirdPersona.title')
            : t('SceneSetting.thirdPersonaInput.title')}
        </h1>
        <p className="mt-[14px] text-[15px] font-medium leading-[18px] text-main-dark-4">
          {isUpdate
            ? t('SceneSetting.changeThirdPersona.subtitle')
            : t('SceneSetting.thirdPersonaInput.subtitle')}
        </p>
        <div className="mt-[38px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[16px]">
            {t('SceneSetting.enterUserInfo.name')}
          </p>
          <div className="px-[16px] py-[17px] bg-secondary-dark rounded-[12px]">
            <InputPlain
              placeholder={t('SceneSetting.thirdPersonaInput.placeholder') as string}
              className="bg-transparent placeholder:text-placeholder-dark !text-main-dark-2"
              value={thirdPersonaName}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e) => onThirdPersonaNameChange(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-[28px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[16px]">
            {t('SceneSetting.enterUserInfo.gender')}
          </p>
          <div className="flex gap-[6px]">
            {GENDER_OPTIONS.map(({ labelKey, value }) => (
              <button
                key={value ?? 'nonBinary'}
                type="button"
                onClick={() => onThirdPersonaGenderChange(value)}
                className={`flex-1 py-[16px] rounded-[8px] text-[17px] leading-[20px] font-semibold flex items-center justify-center gap-[4px] transition-colors border
                   ${
                     thirdPersonaGender === value
                       ? 'border-onPrimary text-onPrimary'
                       : 'text-main-dark-1 border-separator-dark-1'
                   }`}
              >
                {thirdPersonaGender === value && <Check size={16} className="text-onPrimary" />}
                <span className={thirdPersonaGender === value ? 'max-w-[58px] truncate' : ''}>
                  {t(labelKey)}
                </span>
              </button>
            ))}
          </div>
        </div>
        <ul className="text-[12px] text-main-dark-2 leading-[14px] mt-[21px] flex flex-col items-start gap-[4px] list-disc list-inside">
          <li>{t('SceneSetting.thirdPersonaInput.firstInstruction')}</li>
          <li>{t('SceneSetting.thirdPersonaInput.secondInstruction')}</li>
        </ul>
      </div>
      <div
        className={`fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+17px)] transition-all duration-150 ease-in-out ${
          isKeyboardVisible
            ? 'translate-y-full opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100'
        }`}
        aria-hidden={isKeyboardVisible}
      >
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          onClick={onNext}
          disabled={!thirdPersonaName.trim() || thirdPersonaGender === undefined || isLoading}
        >
          {t('next')}
        </Button>
      </div>
    </div>
  );
};

export default ThirdPersonaInputStep;
